import React from 'react';
import { Button } from './ui/button';
import { CheckCircle2, Target, Eye, Award, Users, TrendingUp, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const About = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="container mx-auto px-6">
        {/* Who We Are Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h3 className="text-blue-600 font-semibold text-lg mb-4 tracking-wide uppercase">Who We Are</h3>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Advancing Innovation Through <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Technology</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Code Commerce is a leading technology consulting and software development firm based in Los Angeles, California. We empower businesses across the United States to achieve digital excellence through innovative solutions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwbWFya2V0aW5nfGVufDB8fHx8MTc2MTg2Njg4OXww&ixlib=rb-4.1.0&q=85"
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Founded with a vision to bridge the gap between business challenges and technological solutions, Code Commerce has grown to become a trusted partner for businesses seeking digital transformation. We specialize in IT consulting, custom software development, and comprehensive digital solutions that drive measurable results.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our expertise spans across multiple industries, enabling companies to build innovative, scalable, and high-performing digital solutions. From startups to enterprises, we help organizations leverage cutting-edge technology to stay ahead in today's competitive landscape.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                With a team of 150+ experienced professionals and over a decade of industry experience, we've successfully delivered hundreds of projects, transforming businesses through technology-driven strategies and innovative solutions.
              </p>
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">150+</div>
              <div className="text-gray-700 font-medium">Expert Professionals</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">500+</div>
              <div className="text-gray-700 font-medium">Projects Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">10+</div>
              <div className="text-gray-700 font-medium">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">98%</div>
              <div className="text-gray-700 font-medium">Client Satisfaction</div>
            </div>
          </div>
        </div>

        {/* Mission & Vision Section */}
        <div className="mb-20">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 p-4 rounded-full mr-4">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                To empower businesses with innovative technology solutions that drive growth, efficiency, and competitive advantage. We are committed to delivering excellence through cutting-edge software development, strategic IT consulting, and transformative digital solutions that create lasting value for our clients.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="bg-indigo-100 p-4 rounded-full mr-4">
                  <Eye className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                To be the most trusted technology partner for businesses across America, recognized for our innovation, reliability, and commitment to client success. We envision a future where every business, regardless of size, can harness the power of technology to achieve extraordinary results.
              </p>
            </div>
          </div>
        </div>

        {/* Core Values Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-blue-600 font-semibold text-lg mb-4 tracking-wide uppercase">Our Core Values</h3>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Drives Us <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Forward</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Award className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Excellence</h4>
              <p className="text-gray-700 leading-relaxed">
                We strive for excellence in every project, delivering solutions that exceed expectations and set industry standards.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Client-Centric</h4>
              <p className="text-gray-700 leading-relaxed">
                Your success is our success. We build lasting partnerships through transparency, communication, and dedication.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Innovation</h4>
              <p className="text-gray-700 leading-relaxed">
                We embrace emerging technologies and innovative approaches to solve complex business challenges.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-indigo-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Integrity</h4>
              <p className="text-gray-700 leading-relaxed">
                We operate with honesty and transparency, building trust through ethical practices and reliable delivery.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-indigo-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Quality</h4>
              <p className="text-gray-700 leading-relaxed">
                From code to customer service, quality is embedded in everything we do, ensuring robust and reliable solutions.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Target className="w-8 h-8 text-indigo-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Results-Driven</h4>
              <p className="text-gray-700 leading-relaxed">
                We focus on delivering measurable results that contribute directly to your business objectives and growth.
              </p>
            </div>
          </div>
        </div>

        {/* Why Partner Section */}
        <div>
          <div className="text-center mb-16">
            <h3 className="text-blue-600 font-semibold text-lg mb-4 tracking-wide uppercase">Why Partner with Us?</h3>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Your Trusted Technology <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Partner</span>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              At Code Commerce, we combine technical expertise with business acumen to deliver solutions that drive real impact. Here's what sets us apart.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Proven Track Record</h4>
                  <p className="text-gray-700 leading-relaxed">
                    With over 500+ successful projects and a 98% client satisfaction rate, our results speak for themselves.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">End-to-End Solutions</h4>
                  <p className="text-gray-700 leading-relaxed">
                    From strategy and design to development and support, we provide comprehensive solutions for all your technology needs.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Expert Team</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Our team of certified professionals brings diverse expertise across the latest technologies and industry best practices.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Agile Approach</h4>
                  <p className="text-gray-700 leading-relaxed">
                    We use agile methodologies to ensure flexibility, rapid delivery, and continuous improvement throughout the project lifecycle.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1573285750682-05689540dfbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHx0ZWNobm9sb2d5JTIwd29ya3NwYWNlfGVufDB8fHx8MTc2MTg2Njg2MHww&ixlib=rb-4.1.0&q=85"
                alt="Professional workspace"
                className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-12 shadow-2xl">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h3>
            <p className="text-xl text-blue-50 mb-8 max-w-2xl mx-auto">
              Let's discuss how Code Commerce can help you achieve your technology goals and drive business success.
            </p>
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 text-lg font-semibold"
            >
              Get Started Today
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;